import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './email-confirmation.css';

function EmailConfirmation() {
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying your email confirmation link...');

  const pathToken = useMemo(() => {
    const rawPath = location.pathname.replace(/^\/email-confirmation\/?/, '');
    return decodeURIComponent(rawPath || '').trim();
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    const verify = async () => {
      const queryParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));

      const errorDescription =
        queryParams.get('error_description') || hashParams.get('error_description');
      if (errorDescription) {
        if (!mounted) return;
        setStatus('error');
        setMessage(errorDescription);
        return;
      }

      const tokenHash = queryParams.get('token_hash') || pathToken;
      const verifyType = queryParams.get('type') || hashParams.get('type') || 'signup';

      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: verifyType,
        });

        if (!mounted) return;

        if (error) {
          setStatus('error');
          setMessage(error.message || 'Invalid or expired confirmation link.');
          return;
        }

        setStatus('success');
        setMessage('Your email has been verified. You can now log in to Unlock.');
        return;
      }

      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });

        if (!mounted) return;

        if (error) {
          setStatus('error');
          setMessage(error.message || 'Could not complete email confirmation.');
          return;
        }

        setStatus('success');
        setMessage('Your email has been verified. You can now log in to Unlock.');
        return;
      }

      if (!mounted) return;
      setStatus('idle');
      setMessage('Please open the email confirmation link from your inbox.');
    };

    void verify();

    return () => {
      mounted = false;
    };
  }, [location.hash, location.search, pathToken]);

  return (
    <div className="email-confirmation-page">
      <div className="container">
        <div className="checkmark">{status === 'error' ? '!' : status === 'loading' ? '...' : '?'}</div>
        <h1>
          {status === 'success'
            ? 'Email Confirmed'
            : status === 'error'
              ? 'Confirmation Failed'
              : 'Email Confirmation'}
        </h1>
        <p>{message}</p>
        <Link to="/" className="back-link">
          Back to Unlock App
        </Link>
      </div>
    </div>
  );
}

export default EmailConfirmation;
